import React from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Stack,
  Chip,
} from '@mui/material';
import { Shield, Gavel } from '@mui/icons-material';
import PublicLayout from '../components/PublicLayout';
import { useTranslation } from 'react-i18next';
import {
  marketingBandSx,
  marketingColor,
} from '../theme/marketingTokens';

export const CompliancePage: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string, options?: any) => any };
  const laws = t('public.compliance.laws', { returnObjects: true }) as {
    id: string;
    name: string;
    shortName: string;
    jurisdiction: string;
    description: string;
    effective?: string;
  }[];

  return (
    <PublicLayout>
      <Box>
        <Box
          sx={{
            ...marketingBandSx,
            borderTop: 'none',
            py: 8,
            textAlign: 'center',
          }}
        >
          <Container maxWidth="md">
            <Shield sx={{ fontSize: 64, mb: 2, opacity: 0.9 }} />
            <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
              {t('public.compliance.title')}
            </Typography>
            <Typography variant="h6" sx={{ color: marketingColor.textSecondary }}>
              {t('public.compliance.subtitle')}
            </Typography>
          </Container>
        </Box>

        <Container maxWidth="md" sx={{ py: 8 }}>
          <Paper sx={{ p: { xs: 3, md: 4 }, mb: 3 }}>
            <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
              {t('public.compliance.introTitle')}
            </Typography>
            <Typography variant="body1" paragraph>
              {t('public.compliance.introText')}
            </Typography>
            <Typography variant="body1" paragraph>
              {t('public.compliance.introText2')}
            </Typography>
          </Paper>

          <Paper sx={{ p: { xs: 3, md: 4 }, mb: 3 }}>
            <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
              {t('public.compliance.infraTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('public.compliance.infraText')}
            </Typography>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t('public.compliance.stripeTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('public.compliance.stripeDesc')}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t('public.compliance.firebaseTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('public.compliance.firebaseDesc')}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t('public.compliance.accountSecurityTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('public.compliance.accountSecurityText')}
                </Typography>
              </Paper>
            </Stack>
          </Paper>

          <Paper sx={{ p: { xs: 3, md: 4 }, mb: 3 }}>
            <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
              {t('public.compliance.lawsTitle')}
            </Typography>
            <Stack spacing={4}>
              {laws.map((law) => (
                <Paper key={law.id} variant="outlined" sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Chip
                      icon={<Gavel sx={{ fontSize: 18 }} />}
                      label={law.shortName}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                    <Chip label={law.jurisdiction} variant="outlined" size="small" />
                    {law.effective && (
                      <Chip label={law.effective} variant="outlined" size="small" />
                    )}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {law.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {law.description}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          </Paper>

          <Paper sx={{ p: { xs: 3, md: 4 }, mb: 3, bgcolor: 'action.hover' }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              {t('public.compliance.exerciseRights')}
            </Typography>
            <Typography variant="body2" paragraph>
              {t('public.compliance.exerciseRightsDesc')}
            </Typography>
          </Paper>
        </Container>
      </Box>
    </PublicLayout>
  );
};

export default CompliancePage;
